import isArray from 'lodash/isArray';

import Sale from 'app/src/data/models/Sale';
import PendingPayment from 'app/src/payments/PendingPayment';
import { InvoicingSalePayment } from 'root/apps/QuoteMachineIntegration/state/InvoicingPayload';
import { PaymentSource } from 'root/apps/QuoteMachineIntegration/state/PaymentSource';

import { PendingPaymentMap } from '../state/PendingPaymentMap';

export function ParsePendingPaymentMap(paymentMap: PendingPaymentMap): InvoicingSalePayment[] {
  return Object.values(paymentMap)
    .filter((pmt: PendingPayment) => pmt.paymentTypeID !== 4)
    .map((pmt: PendingPayment) => {
      const paymentObject: InvoicingSalePayment = {
        paymentTypeName: pmt.model.attributes.PaymentType.name,
        amount: pmt.model.attributes.amount,
        tipAmount: '0',
        surchargeAmount: '0',
        paymentTypeId: pmt.paymentTypeID.toString(),
        source: PaymentSource.BACK_OFFICE,
        currency: window.merchantos.locale.currency_code,
        paymentID: '',
        cashRoundingDelta: '0',
      };
      return paymentObject;
    });
}

export function ParsePendingCreditAccountPaymentMap(
  paymentMap: PendingPaymentMap
): InvoicingSalePayment[] {
  const CREDIT_ACCOUNT_PAYMENT_TYPE_CODE = 'SCA';
  const CREDIT_ACCOUNT_PAYMENT_TYPE_ID = 4;

  return Object.values(paymentMap)
    .filter((pmt: PendingPayment) => {
      if (pmt.model?.attributes?.PaymentType?.code) {
        return pmt.model.attributes.PaymentType.code === CREDIT_ACCOUNT_PAYMENT_TYPE_CODE;
      }

      return pmt.paymentTypeID === CREDIT_ACCOUNT_PAYMENT_TYPE_ID;
    })
    .map((pmt: PendingPayment) => {
      return parsePendingPaymentToInvoicingSalePayment(pmt);
    });
}

function parsePendingPaymentToInvoicingSalePayment(pmt: PendingPayment): InvoicingSalePayment {
  return {
    paymentTypeName: pmt.model.attributes.PaymentType.name,
    amount: pmt.model.attributes.amount,
    tipAmount: '0',
    surchargeAmount: '0',
    paymentTypeId: pmt.paymentTypeID.toString(),
    source: PaymentSource.BACK_OFFICE,
    currency: window.merchantos.locale.currency_code,
    paymentID: '',
  };
}

export function ParseProcessedPayments(salePayments: SalePayment[]): InvoicingSalePayment[] {
  if (!salePayments) {
    return [];
  }

  return salePayments.map((salePayment) => {
    const {
      PaymentType: { paymentTypeID: paymentTypeId, name: paymentTypeName },
      amount,
      tipAmount,
      surchargeAmount,
      paymentID,
    } = salePayment;
    return {
      paymentTypeName,
      paymentID: paymentID.toString(),
      amount,
      tipAmount: tipAmount?.toString() ?? '0',
      surchargeAmount: surchargeAmount?.toString() ?? '0',
      paymentTypeId: paymentTypeId.toString(),
      source: PaymentSource.BACK_OFFICE,
      currency: window.merchantos.locale.currency_code,
    };
  });
}

export function getProcessedPaymentsFromSale(sale: Sale) {
  let salePayments: SalePayment | SalePayment[] = sale.attributes.SalePayments?.SalePayment ?? [];

  if (!isArray(salePayments)) {
    salePayments = [salePayments];
  }

  // The interface specifies that archived is a stringified boolean, but the observed data is in actual boolean format.
  // Adding both cases here just to be certain we don't run into issues depending on the format received.
  return salePayments.filter((payment) => !payment.archived || payment.archived === 'false');
}

export function getSalePaymentsForInvoice(
  salePayments: SalePayment[],
  pendingPayments: PendingPaymentMap
): InvoicingSalePayment[] {
  return [
    ...ParseProcessedPayments(salePayments),
    ...ParsePendingPaymentMap(pendingPayments),
  ].filter(filterSalePayments);
}

export function filterSalePayments(payment: InvoicingSalePayment): boolean {
  const isCreditAccountPayment = payment.paymentTypeId === '4';
  const isGiftCardCreationPayment = payment.paymentTypeId === '5' && Number(payment.amount) <= 0;
  return !isCreditAccountPayment && !isGiftCardCreationPayment;
}

export function GetPendingCreditAccountPaymentAmount(paymentMap: PendingPaymentMap): number {
  const payments = ParsePendingCreditAccountPaymentMap(paymentMap);
  return payments.reduce((sum, payment) => sum + Number(payment.amount), 0);
}

export function BuildPaymentDetailsURL(
  accountRef: string,
  locationRef: string,
  chargeId?: string
): string {
  const baseUrl = window.location.origin;

  if (accountRef && locationRef && chargeId) {
    return `${baseUrl}/payments/lightspeed-payments/merchant-portal/lsr:${accountRef}-${locationRef}/st-${chargeId}`;
  }

  return `${baseUrl}/payments/lightspeed-payments/merchant-portal`;
}
